/**
 * ActiveCardShell — wrapper for the four Phase 1 cards. Gives them a
 * consistent header (card ID + title + status chip) and body padding
 * so they feel like native NDS platform cards without duplicating
 * boilerplate across the four files.
 */
import React from "react";
import { Pencil, Zap } from "lucide-react";
import { useDemoBrand } from "@/lib/demoBrand";

export default function ActiveCardShell({ id, title: defaultTitle, kicker, Icon, children, actions }) {
  const { cardCopy, isAdmin, onEditCard } = useDemoBrand();
  const copy = cardCopy[id];
  const title = copy?.title || defaultTitle;
  return (
    <section
      data-testid={`veroogle-card-${id.toLowerCase()}`}
      className="glass-card rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/8 bg-gradient-to-r from-brand-cyan/5 via-transparent to-brand-emerald/5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center shrink-0">
            {Icon ? <Icon className="w-5 h-5 text-navy-950" /> : <Zap className="w-5 h-5 text-navy-950" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-brand-cyan">{id}</span>
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">· {kicker}</span>
            </div>
            <h3 className="font-semibold text-white text-base md:text-lg leading-tight truncate" data-testid={`veroogle-card-title-${id.toLowerCase()}`}>{title}</h3>
            {copy?.hint && <p className="text-slate-400 text-xs mt-0.5 truncate" data-testid={`veroogle-card-hint-${id.toLowerCase()}`}>{copy.hint}</p>}
          </div>
          {isAdmin && copy && onEditCard && (
            <button type="button" onClick={() => onEditCard(copy)} aria-label="Edit card copy"
              data-testid={`veroogle-card-edit-${id.toLowerCase()}`}
              className="p-1.5 rounded-md text-slate-500 hover:text-brand-cyan hover:bg-white/5 transition-colors shrink-0">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="hidden md:flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-emerald-400">Active</span>
        </div>
      </header>
      <div className="p-5">{children}</div>
      {actions && (
        <footer className="px-5 py-3 border-t border-white/8 bg-white/[0.02] flex items-center justify-end gap-2 flex-wrap">
          {actions}
        </footer>
      )}
    </section>
  );
}
